import { Component, OnInit, Inject, PLATFORM_ID, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../../core/auth/auth.service';
import { AuthService as SessionAuthService } from '../../../services/auth/auth.service';
import { TokenManagerService } from '../../../services/auth/token-manager.service';
import { ThemeService } from '../../../core/services/theme.service';

@Component({
  selector: 'app-user-actions',
  templateUrl: './user-actions.component.html',
  styleUrl: './user-actions.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserActionsComponent implements OnInit {
  items: MenuItem[] | undefined = [];
  userName: string = '';
  userEmail: string = '';
  isMobile = false;
  drawerVisible = false;
  showThemeSubmenu = false;

  constructor(
    private route: Router,
    private authService: AuthService,
    private sessionAuthService: SessionAuthService,
    private tokenManager: TokenManagerService,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    this.buildMenu();
    if (isPlatformBrowser(this.platformId)) {
      this.checkScreenSize();
      window.addEventListener('resize', () => this.checkScreenSize());
    }
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth <= 768;
    this.cdr.markForCheck();
  }

  onAvatarClick(event: Event): void {
    if (this.isMobile) {
      this.drawerVisible = true;
    }
    // En desktop el tieredMenu se maneja con #userActions.toggle()
  }

  setTheme(theme: 'light' | 'dark' | 'system'): void {
    this.themeService.setTheme(theme);
    this.showThemeSubmenu = false;
    this.drawerVisible = false;
  }

  /**
   * Construye el menú con notificaciones y opciones de usuario
   */
  private buildMenu(): void {
    this.items = [
      {
        label: 'Mi perfil',
        icon: 'pi pi-user',
        command: () => {
          this.onProfileClick();
        },
      },
      { separator: true },
      {
        label: 'Tema',
        icon: 'pi pi-palette',
        items: [
          {
            label: 'Modo Claro',
            icon: 'pi pi-sun',
            command: () => this.themeService.setTheme('light'),
          },
          {
            label: 'Modo Oscuro',
            icon: 'pi pi-moon',
            command: () => this.themeService.setTheme('dark'),
          },
          {
            label: 'Sistema',
            icon: 'pi pi-desktop',
            command: () => this.themeService.setTheme('system'),
          },
        ],
      },
      { separator: true },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => {
          this.onLogoutClick();
        },
      },
    ];
  }

  /**
   * Carga la información del usuario desde el AuthService y decodifica el JWT
   */
  private loadUserInfo(): void {
    const user = this.authService.getCurrentUser();
    if (user && user.idToken) {
      try {
        const decodedToken = this.decodeToken(user.idToken);
        this.userName = decodedToken.name || decodedToken.given_name || decodedToken.email?.split('@')[0] || 'Usuario';
        this.userEmail = decodedToken.email || '';
      } catch (error) {
        console.error('Error al decodificar el token:', error);
        this.userName = 'Usuario';
        this.userEmail = user.email || '';
      }
    }
  }

  /**
   * Decodifica un JWT (parte payload)
   */
  private decodeToken(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Token inválido');
      }
      const decodedPayload = atob(parts[1]);
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Error al decodificar JWT:', error);
      return {};
    }
  }

  onProfileClick() {
    this.drawerVisible = false;
    this.route.navigate(['/profile']);
  }

  onSettingsClick() {
    console.log('Settings clicked');
    // Lógica para el clic en "Settings"
  }

  async onLogoutClick() {
    try {
      // Detener el monitoreo de tokens
      this.tokenManager.stopTokenRefreshMonitoring();

      // Limpiar sesión local gestionada por backend
      this.authService.logout();
      this.sessionAuthService.clearSession();
    } catch (error) {
      console.error('Error durante el logout:', error);
    } finally {
      // Siempre redirigir al login (ruta absoluta)
      this.route.navigate(['/login'], { replaceUrl: true });
    }
  }

  onUserManagementClick() {
    this.route.navigate(['user-management']);
  }
}
